import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Search, Plus, Pencil, Building2, Trash2, Network } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DeleteButton } from "@/components/DeleteButton";

const ZONES = ["North", "South", "West", "Exports"];
const ZONE_STYLE = {
  North: "bg-blue-50 text-blue-700 border-blue-200",
  South: "bg-emerald-50 text-emerald-700 border-emerald-200",
  West: "bg-orange-50 text-orange-700 border-orange-200",
  Exports: "bg-violet-50 text-violet-700 border-violet-200",
};

function DealerDialog({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const isEdit = !!initial?.id;

  const openChange = (v) => { if (v) setF(initial || { zone: "" }); onOpenChange(v); };

  const save = async () => {
    if (!f.name?.trim()) { toast.error("Dealer name is required"); return; }
    if (!f.zone) { toast.error("Select a zone"); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/dealers/${initial.id}`, f);
      else await api.post("/dealers", f);
      toast.success(isEdit ? "Dealer updated" : "Dealer added");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const T = ([k, label]) => (
    <div className="space-y-1.5" key={k}>
      <Label className="text-xs font-medium text-slate-700">{label}</Label>
      <Input value={f[k] || ""} onChange={(e) => set(k, e.target.value)} className="h-9" data-testid={`dealer-${k}`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={openChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dealer-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit Dealer" : "Add Dealer"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Dealer Name <span className="text-red-500">*</span></Label>
            <Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} className="h-9" data-testid="dealer-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Zone <span className="text-red-500">*</span></Label>
            <Select value={f.zone || ""} onValueChange={(v) => set("zone", v)}>
              <SelectTrigger className="h-9" data-testid="dealer-zone"><SelectValue placeholder="Select zone" /></SelectTrigger>
              <SelectContent>{ZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {[["sub_region", "Sub-region (e.g. Gujarat, East)"], ["city", "City"],
            ["salesperson_name", "UniVicoustic Salesperson"], ["contact_person", "Contact Person"],
            ["phone", "Phone"], ["email", "Email"]].map(T)}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-slate-700">Notes</Label>
          <Textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="dealer-notes" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dealer-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="dealer-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}{isEdit ? "Save Changes" : "Add Dealer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Dealers() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [zone, setZone] = useState("");
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(null); // null | {} (new) | dealer (edit)

  const canEdit = user?.role !== "management" && user?.role !== "dealer" && user?.role !== "factory";
  const canDeleteDealer = ["super_admin", "sales_head"].includes(user?.role);

  const { data, isLoading } = useQuery({
    queryKey: ["dealers-page", zone, q],
    queryFn: async () => (await api.get("/dealers", { params: { zone, q } })).data,
    keepPreviousData: true,
  });
  const items = data?.items || [];

  const refresh = () => qc.invalidateQueries({ queryKey: ["dealers-page"] });

  const del = async (id) => {
    try { await api.delete(`/dealers/${id}`); toast.success("Dealer deleted"); refresh(); }
    catch (e) { toast.error(apiError(e)); throw e; }
  };

  return (
    <div>
      <PageHeader title="Dealers" subtitle="Zone-wise channel partner master — powers the cascading dealer dropdown on leads & opportunities.">
        {["super_admin", "sales_head"].includes(user?.role) && (
          <Button variant="outline" className="gap-1.5" onClick={() => (window.location.href = "/dealer-assignment")} data-testid="goto-dealer-assignment-btn">
            <Network className="h-4 w-4" /> Dealer Assignment
          </Button>
        )}
        {canEdit && (
          <Button onClick={() => setDialog({})} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="add-dealer-btn">
            <Plus className="h-4 w-4" /> Add Dealer
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex rounded-md border border-slate-200 overflow-hidden">
          {["", ...ZONES].map((z) => (
            <button key={z || "all"} onClick={() => setZone(z)}
              className={`px-3 h-9 text-sm ${zone === z ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid={`dealer-zone-tab-${z || "all"}`}>
              {z || "All zones"}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px] max-w-xs ml-auto">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dealer…" className="h-9 pl-9" data-testid="dealer-search" />
        </div>
        <span className="text-xs text-slate-400">{items.length} dealer{items.length === 1 ? "" : "s"}</span>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["Dealer", "Zone", "Sub-region", "City", "Salesperson", "Contact", ""].map((h) => (
                  <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                ))}
              </TableRow></TableHeader>
              <TableBody>
                {items.map((d) => (
                  <TableRow key={d.id} data-testid={`dealer-row-${d.id}`}>
                    <TableCell className="font-medium text-slate-800 min-w-[200px]">
                      <span className="flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-slate-300" />{d.name}</span>
                    </TableCell>
                    <TableCell><span className={`text-xs font-semibold px-2 py-0.5 rounded border ${ZONE_STYLE[d.zone] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{d.zone}</span></TableCell>
                    <TableCell className="text-slate-500">{d.sub_region && d.sub_region !== d.zone ? d.sub_region : "—"}</TableCell>
                    <TableCell className="text-slate-600">{d.city || "—"}</TableCell>
                    <TableCell className="text-slate-600">{d.salesperson_name || "—"}</TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {d.contact_person || d.phone || d.email ? (
                        <span>{d.contact_person}{d.phone ? ` · ${d.phone}` : ""}{d.email ? ` · ${d.email}` : ""}
                          {d.marketing_opt_out && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-semibold border border-red-100" data-testid={`dealer-optout-${d.id}`}>Do not email</span>}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {canEdit && (
                        <button onClick={() => setDialog(d)} className="text-slate-400 hover:text-slate-700 p-1" title="Edit" data-testid={`dealer-edit-${d.id}`}>
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {canDeleteDealer && (
                        <DeleteButton testId={`dealer-delete-${d.id}`} title="Delete this dealer?" description="Leads keep the dealer name, but it disappears from the dropdown." onConfirm={() => del(d.id)} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">No dealers found.</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>

      <DealerDialog open={dialog !== null} onOpenChange={(v) => !v && setDialog(null)} initial={dialog} onSaved={refresh} />
    </div>
  );
}
